import React, { useEffect } from "react";
import { Link } from "react-router-dom";

import {
  ArrowRight,
  ArrowUpRight,
  Code2,
  Smartphone,
  BrainCircuit,
  Workflow,
  Building2,
  ShoppingBag,
  HeartPulse,
  GraduationCap,
  Factory,
  Database,
} from "lucide-react";

import "./Overview.css";

function Overview() {
  /* =====================================================
     GLOBAL SCROLL REVEAL ANIMATION

     Every element with:
     data-reveal

     will animate when it enters the viewport.

     Services intentionally use 10% visibility.
  ===================================================== */

  useEffect(() => {
    const revealElements = document.querySelectorAll(
      "[data-reveal]"
    );

    if (!revealElements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          entry.target.classList.add("is-visible");

          observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -40px 0px",
      }
    );

    revealElements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  /* =====================================================
     SERVICES
  ===================================================== */

  const services = [
    {
      number: "01",
      icon: <Code2 size={22} />,
      title: "Web Development",
      text:
        "Professional websites and web applications built around your business requirements.",
      link: "/services/web-development",
    },
    {
      number: "02",
      icon: <Smartphone size={22} />,
      title: "Mobile App Development",
      text:
        "Reliable and user-friendly mobile applications for practical business needs.",
      link: "/services/mobile-development",
    },
    {
      number: "03",
      icon: <BrainCircuit size={22} />,
      title: "AI & Machine Learning",
      text:
        "Practical AI and machine learning solutions for data-driven applications.",
      link: "/services/ai-automation",
    },
    {
      number: "04",
      icon: <Workflow size={22} />,
      title: "AI Automation",
      text:
        "Business workflows designed to reduce repetitive work and improve efficiency.",
      link: "/services/ai-automation",
    },
  ];

  /* =====================================================
     SELECTED PROJECTS
  ===================================================== */

  const projects = [
    {
      number: "01",
      category: "WEB DEVELOPMENT",
      title: "Shreepad Enterprises",
      challenge:
        "The business needed a professional digital presence to showcase its packaging products and make customer enquiries easier.",
      solution:
        "Built a clean, responsive enquiry website that presents products clearly and gives customers a direct way to connect with the business.",
      technology: [
        "React",
        "JavaScript",
        "Responsive UI",
      ],
      impact:
        "A clearer product presentation and a simpler digital channel for customer enquiries.",
    },
    {
      number: "02",
      category: "WEB DEVELOPMENT",
      title: "Bhimashankar Agro",
      challenge:
        "The company needed to clearly communicate its cold-storage infrastructure, agricultural services and business capabilities.",
      solution:
        "Created a professional responsive website focused on cold-chain services, storage capabilities and customer communication.",
      technology: [
        "React",
        "JavaScript",
        "Responsive UI",
      ],
      impact:
        "Made the company's services and cold-storage capabilities easier for potential customers to understand.",
    },
    {
      number: "03",
      category: "JAVA FULL STACK",
      title: "Employee & Project Management System",
      challenge:
        "Managing employees, projects, tasks and assignments manually can make information difficult to organize and track.",
      solution:
        "Developed a full-stack management platform that centralizes employees, projects, tasks and assignments in one system.",
      technology: [
        "Java",
        "Spring Boot",
        "MySQL",
        "React",
      ],
      impact:
        "Centralized project and employee information into a structured digital management system.",
    },
  ];

  /* =====================================================
     INDUSTRIES
  ===================================================== */

  const industries = [
    {
      icon: <Building2 size={21} />,
      title: "Packaging & Manufacturing",
    },
    {
      icon: <ShoppingBag size={21} />,
      title: "Agro & Cold Storage",
    },
    {
      icon: <HeartPulse size={21} />,
      title: "Business Management",
    },
    {
      icon: <GraduationCap size={21} />,
      title: "Small Businesses",
    },
    {
      icon: <Factory size={21} />,
      title: "Manufacturing",
    },
    {
      icon: <Database size={21} />,
      title: "Finance & Operations",
    },
  ];

  return (
    <main className="overview-page">

      {/* =====================================================
          HERO
      ===================================================== */}

      <section className="overview-hero">

        <div className="overview-hero-grid"></div>

        <div className="overview-hero-orbit overview-orbit-one"></div>
        <div className="overview-hero-orbit overview-orbit-two"></div>

        <div className="overview-hero-line overview-hero-line-one"></div>
        <div className="overview-hero-line overview-hero-line-two"></div>

        <div className="overview-container overview-hero-content">

          <div className="overview-eyebrow hero-reveal hero-delay-1">
            <span className="overview-eyebrow-line"></span>

            <span>NEXIS FOLD</span>

            <span className="overview-eyebrow-dot">•</span>

            <span>
              SOFTWARE & DIGITAL SOLUTIONS
            </span>
          </div>

          <div className="overview-hero-layout">

            {/* HERO LEFT */}

            <div className="overview-hero-main">

              <div className="overview-hero-kicker hero-reveal hero-delay-2">
                DIGITAL ENGINEERING
              </div>

              <h1 className="hero-reveal hero-delay-3">
                Websites and business
                <br />
                <span>software that work.</span>
              </h1>

              <p className="hero-reveal hero-delay-4">
                We help growing businesses get found online and run more smoothly
                with responsive websites, practical business software and automation.
                Based in Pune, we build around your actual needs—not unnecessary complexity.
              </p>

              <div className="overview-hero-actions hero-reveal hero-delay-5">

                <Link
                  to="/contact"
                  className="overview-btn overview-btn-primary"
                >
                  <span>Start a Project</span>
                  <ArrowRight size={17} />
                </Link>

                <a
                  href="#selected-projects"
                  className="overview-btn overview-btn-secondary"
                >
                  <span>View Our Work</span>
                  <ArrowRight size={17} />
                </a>

              </div>

              <div className="overview-hero-note hero-reveal hero-delay-6">

                <span className="overview-hero-note-line"></span>

                <span>Software Development</span>

                <b>•</b>

                <span>AI</span>

                <b>•</b>

                <span>Automation</span>

                <b>•</b>

                <span>Digital Products</span>

              </div>

            </div>

            {/* HERO RIGHT */}

            <div className="overview-hero-visual hero-reveal hero-delay-4">

              <div className="overview-hero-tech-frame">

                <div className="overview-tech-corner corner-top-left"></div>
                <div className="overview-tech-corner corner-top-right"></div>
                <div className="overview-tech-corner corner-bottom-left"></div>
                <div className="overview-tech-corner corner-bottom-right"></div>

                <div className="overview-hero-image">

                  <img
                    src="/images/overview-hero2.png"
                    alt="Technology workspace"
                  />

                </div>

                <div className="overview-image-scan"></div>

              </div>

              <div className="overview-floating-label overview-floating-label-one">
                <span className="status-dot"></span>
                DIGITAL SYSTEMS
              </div>

              <div className="overview-floating-label overview-floating-label-two">
                <span>01</span>
                TECHNOLOGY
              </div>

            </div>

          </div>

        </div>

        <div className="overview-scroll-indicator">
          <span></span>
          <small>SCROLL TO EXPLORE</small>
        </div>

      </section>


      {/* =====================================================
          ABOUT
      ===================================================== */}

      <section className="overview-intro">

        <div className="overview-container">

          <div className="overview-intro-grid">

            <div
              className="overview-intro-heading"
              data-reveal="left"
            >

              <span className="overview-section-label">
                ABOUT NEXIS FOLD
              </span>

              <h2>
                We build digital
                <br />
                <span>solutions with purpose.</span>
              </h2>

            </div>

            <div
              className="overview-intro-text"
              data-reveal="right"
            >

              <p>
                Nexis Fold is a software development company
                focused on building practical digital products
                for businesses. We combine design, development
                and modern technology to turn ideas and business
                requirements into working solutions.
              </p>

              <Link
                to="/about"
                className="overview-text-link"
              >
                <span>More about Nexis Fold</span>
                <ArrowUpRight size={16} />
              </Link>

            </div>

          </div>


          {/* MOVING SPECIALTIES */}

          <div
            className="overview-specialties-wrapper"
            data-reveal="up"
          >

            <div className="overview-specialties">

              <div className="overview-specialty-item">
                <strong>Software</strong>
                <span>
                  Web & application development
                </span>
              </div>

              <div className="overview-specialty-item">
                <strong>AI</strong>
                <span>
                  Intelligent digital solutions
                </span>
              </div>

              <div className="overview-specialty-item">
                <strong>Automation</strong>
                <span>
                  Connected business workflows
                </span>
              </div>

              <div className="overview-specialty-item">
                <strong>Digital Products</strong>
                <span>
                  Solutions built around users
                </span>
              </div>

              {/* DUPLICATE FOR SEAMLESS MARQUEE */}

              <div className="overview-specialty-item">
                <strong>Software</strong>
                <span>
                  Web & application development
                </span>
              </div>

              <div className="overview-specialty-item">
                <strong>AI</strong>
                <span>
                  Intelligent digital solutions
                </span>
              </div>

              <div className="overview-specialty-item">
                <strong>Automation</strong>
                <span>
                  Connected business workflows
                </span>
              </div>

              <div className="overview-specialty-item">
                <strong>Digital Products</strong>
                <span>
                  Solutions built around users
                </span>
              </div>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          SERVICES
          10% VISIBILITY TRIGGERS ANIMATION
      ===================================================== */}

      <section
        className="overview-services"
        data-reveal-section="services"
      >

        <div className="overview-services-bg"></div>

        <div className="overview-container">

          <div className="overview-section-header">

            <div
              data-reveal="left"
              className="services-heading-block"
            >

              <span className="overview-section-label">
                OUR SERVICES
              </span>

              <h2>
                Technology for
                <br />
                <span>real requirements.</span>
              </h2>

            </div>

            <div
              data-reveal="right"
              className="services-description-block"
            >

              <p>
                From websites and applications to AI and
                automation, we provide focused technology
                services based on what your business actually
                needs.
              </p>

              <Link
                to="/services"
                className="overview-text-link"
              >
                <span>Explore all services</span>
                <ArrowRight size={16} />
              </Link>

            </div>

          </div>


          {/* SERVICES GRID */}

          <div className="overview-services-grid">

            {services.map((service, index) => (

              <Link
                to={service.link}
                className={`overview-service-card service-card-${index + 1}`}
                key={service.number}
                data-reveal="service"
              >

                <div className="service-card-grid"></div>

                <div className="service-card-light"></div>

                <div className="service-card-glow"></div>

                <div className="overview-card-top">

                  <div className="overview-service-icon">
                    {service.icon}
                  </div>

                  <span className="overview-service-number">
                    {service.number}
                  </span>

                </div>

                <div className="service-card-content">

                  <h3>{service.title}</h3>

                  <p>{service.text}</p>

                </div>

                <div className="overview-card-link">

                  <span>Explore service</span>

                  <span className="service-arrow">
                    <ArrowUpRight size={16} />
                  </span>

                </div>

                <div className="service-card-bottom-line"></div>

              </Link>

            ))}

          </div>

        </div>

      </section>


      {/* =====================================================
          PROJECTS
      ===================================================== */}

      <section
        className="overview-projects"
        id="selected-projects"
      >

        <div className="overview-project-background"></div>

        <div className="overview-container">

          <div
            className="overview-projects-header"
            data-reveal="up"
          >

            <div>

              <span className="overview-section-label">
                SELECTED PROJECTS
              </span>

              <h2>
                Work that
                <br />
                <span>solves real problems.</span>
              </h2>

              <p className="overview-projects-intro">
                A few examples of how we turn business
                requirements into practical digital solutions.
              </p>

            </div>

          </div>


          <div className="overview-project-cards">

            {projects.map((project, index) => (

              <article
                className={`overview-project-card-simple project-card-${index + 1}`}
                key={project.number}
                data-reveal="project"
              >

                <div className="project-card-top-line"></div>

                <div className="overview-project-card-top">

                  <span className="overview-project-number">
                    {project.number}
                  </span>

                </div>

                <div className="overview-project-card-content">

                  <span className="overview-project-category">
                    {project.category}
                  </span>

                  <h3>{project.title}</h3>


                  <div className="overview-project-info">

                    <span className="overview-project-info-label">
                      THE CHALLENGE
                    </span>

                    <p>{project.challenge}</p>

                  </div>


                  <div className="overview-project-info">

                    <span className="overview-project-info-label">
                      THE SOLUTION
                    </span>

                    <p>{project.solution}</p>

                  </div>


                  <div className="overview-project-tech">

                    <span className="overview-project-info-label">
                      TECHNOLOGY
                    </span>

                    <div className="overview-project-tech-list">

                      {project.technology.map((tech) => (

                        <span
                          key={tech}
                          className="overview-project-tech-tag"
                        >
                          {tech}
                        </span>

                      ))}

                    </div>

                  </div>


                  <div className="overview-project-impact">

                    <span className="overview-project-info-label">
                      IMPACT
                    </span>

                    <p>{project.impact}</p>

                  </div>

                </div>

              </article>

            ))}

          </div>


          <div
            className="overview-projects-footer"
            data-reveal="up"
          >

            <span className="overview-projects-count">
              03 Projects
            </span>

            <span className="overview-projects-line"></span>

            <p>
              Web, software and business-focused digital
              solutions.
            </p>

          </div>

        </div>

      </section>


      {/* =====================================================
          INDUSTRIES
      ===================================================== */}

      <section className="overview-industries">

        <div className="overview-industries-grid"></div>

        <div className="overview-container">

          <div className="overview-industry-layout">

            <div
              className="overview-industry-content"
              data-reveal="left"
            >

              <span className="overview-section-label">
                INDUSTRIES
              </span>

              <h2>
                Technology that
                <br />
                <span>fits the context.</span>
              </h2>

              <p>
                Every business has different customers,
                processes and challenges. We understand the
                context first and then build technology around it.
              </p>

              <Link
                to="/industries"
                className="overview-text-link"
              >
                <span>Explore industries</span>
                <ArrowRight size={16} />
              </Link>

            </div>


            <div className="overview-industry-grid">

              {industries.map((industry, index) => (

                <Link
                  to="/industries"
                  className={`overview-industry-item industry-item-${index + 1}`}
                  key={industry.title}
                  data-reveal="industry"
                >

                  <div className="industry-icon">
                    {industry.icon}
                  </div>

                  <span>
                    {industry.title}
                  </span>

                  <ArrowUpRight size={16} />

                  <div className="industry-item-line"></div>

                </Link>

              ))}

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          CTA
      ===================================================== */}

      <section className="overview-cta">

        <div className="overview-cta-grid"></div>

        <div className="overview-cta-orbit"></div>

        <div className="overview-container">

          <div
            className="overview-cta-box"
            data-reveal="up"
          >

            <div className="overview-cta-content">

              <span className="overview-section-label">
                HAVE A PROJECT IN MIND?
              </span>

              <h2>
                Let's discuss
                <br />
                <span>what you're building.</span>
              </h2>

              <p>
                Tell us about your idea, business requirement
                or existing system. We'll start by understanding
                the problem and discussing the right direction.
              </p>

            </div>


            <Link
              to="/contact"
              className="overview-btn overview-btn-primary overview-cta-button"
            >
              <span>Start a Conversation</span>
              <ArrowRight size={17} />
            </Link>

          </div>

        </div>

      </section>

    </main>
  );
}

export default Overview;